// pages/organization/OrgResultsPage.jsx
import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { httpRequest } from "../../utils/httpRequest";
import ResultCard from "../../components/result/ResultCard";
import { useTranslation } from "react-i18next";

const OrgResultsPage = () => {
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [examFilter, setExamFilter] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchResults(page, examFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, examFilter]);

  const fetchResults = async (page = 1, exam = "") => {
    try {
      setLoading(true);
      const res = await httpRequest(
        "GET",
        `/result/list?page=${page}&limit=18${
          examFilter ? `&exam=${examFilter}` : ""
        }`,
        {},
        { "x-access-token": user.token }
      );
      const data = res?.data;
      console.log(data);
      setResults(data?.data || []);
      setTotal(data?.total || 0);
    } catch (error) {
      console.error("Failed to fetch results", error);
    } finally {
      setLoading(false);
    }
  };

  const isRtl = i18n.language.includes("fa");
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  const pages = Math.max(1, Math.ceil(total / 18));

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_results.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("org_results.page_header")}
            </h1>

            {/* فیلتر سادهٔ آزمون (اختیاری: یک Select از لیست آزمون‌ها) */}
            <div className="flex items-center gap-2">
              <input
                className="border rounded px-3 py-1 text-sm"
                placeholder={t("org_results.filter_exam_placeholder")}
                value={examFilter}
                onChange={(e) => setExamFilter(e.target.value.trim())}
              />
            </div>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">
              {t("org_results.loading_message")}
            </p>
          ) : results.length > 0 ? (
            <>
              {/* Table View */}
              <div className="overflow-x-auto rounded-xl border bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 ">
                        {t("org_results.col_exam") || "Exam"}
                      </th>
                      <th className="px-3 py-2 ">
                        {t("org_results.col_user") || "Candidate"}
                      </th>
                      <th className="px-3 py-2 text-right">
                        {t("org_results.col_score") || "Score"}
                      </th>
                      <th className="px-3 py-2 text-right">%</th>
                      <th className="px-3 py-2 ">
                        {t("org_results.col_started") || "Started"}
                      </th>
                      <th className="px-3 py-2 ">
                        {t("org_results.col_finished") || "Finished"}
                      </th>
                      <th className="px-3 py-2 text-right">
                        {t("org_results.col_duration") || "Duration"}
                      </th>
                      <th className="px-3 py-2 ">
                        {t("org_results.col_status") || "Status"}
                      </th>
                      <th className="px-3 py-2 text-right">
                        {t("org_results.col_actions") || "Actions"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {results.map((res) => {
                      const started = res.startedAt
                        ? new Date(res.startedAt)
                        : null;
                      const finished = res.finishedAt
                        ? new Date(res.finishedAt)
                        : null;
                      const durationSec =
                        started && finished
                          ? Math.max(0, Math.round((finished - started) / 1000))
                          : null;
                      const toMMSS = (s) => {
                        if (s == null) return "—";
                        const m = Math.floor(s / 60)
                          .toString()
                          .padStart(2, "0");
                        const r = (s % 60).toString().padStart(2, "0");
                        return `${m}:${r}`;
                      };
                      const fmtDate = (d) =>
                        d
                          ? `${d.getFullYear()}-${String(
                              d.getMonth() + 1
                            ).padStart(2, "0")}-${String(d.getDate()).padStart(
                              2,
                              "0"
                            )} ${String(d.getHours()).padStart(
                              2,
                              "0"
                            )}:${String(d.getMinutes()).padStart(2, "0")}`
                          : "—";

                      return (
                        <tr key={res._id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">{res.examTitle || "—"}</td>
                          <td className="px-3 py-2">{res.userName || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            {res.score ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {Math.round(res.percentage || 0)}
                          </td>
                          <td className="px-3 py-2">{fmtDate(started)}</td>
                          <td className="px-3 py-2">{fmtDate(finished)}</td>
                          <td className="px-3 py-2 text-right">
                            {toMMSS(durationSec)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs
                    ${
                      res.status === "scored"
                        ? "bg-emerald-50 text-emerald-700"
                        : res.status === "submitted"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                            >
                              {res.status || "—"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex gap-2">
                              <button
                                onClick={() =>
                                  (window.location.href = `/results/${res._id}`)
                                }
                                className="px-2 py-1 rounded border hover:bg-gray-50"
                              >
                                {t("common.view") || "View"}
                              </button>
                              {/* اگر نیاز به تصحیح داشت */}
                              {res.status === "submitted" && (
                                <button
                                  onClick={() =>
                                    (window.location.href = `/organization/grading/${
                                      res.attemptId || res._id
                                    }`)
                                  }
                                  className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                                >
                                  {t("org_results.action_grade") || "Grade"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={`px-3 py-1 rounded border ${
                      page <= 1
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-white"
                    }`}
                  >
                    {t("common.prev")}
                  </button>
                  <div className="text-sm">
                    {page} / {pages}
                  </div>
                  <button
                    disabled={page >= pages}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    className={`px-3 py-1 rounded border ${
                      page >= pages
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-white"
                    }`}
                  >
                    {t("common.next")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">
              {t("org_results.empty_state_message")}
            </p>
          )}
        </main>
      </div>
    </div>
  );
};

export default OrgResultsPage;
